import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { DeviceFilters } from '../../../../model/filters/device-filters';
import { translate } from '@ngneat/transloco';
import { EntityWithUuid } from '../../../../model/custom/entity-with-uuid';
import { Device } from '../../../../model/device';
import { Organization } from '../../../../model/organization';
import { DevicesService } from '../../../../services/devices.service';
import { TenantsService } from '../../../../services/tenants.service';
import { DeviceCategory } from '../../../../model/device-category';
import { Tenant } from '../../../../model/tenant';
import { SortOrder } from '../../../../model/sort-order';

@Component({
  selector: 'app-devices-step',
  templateUrl: './devices-step.component.html',
  styleUrls: ['./devices-step.component.scss']
})
export class DevicesStepComponent implements OnInit {

  @Input() tenant: Tenant;
  @Input() organization: Organization;
  @Input() selectionMode: 'single' | 'multiple';
  @Input() deviceSelection: EntityWithUuid | EntityWithUuid[];
  @Input() deviceFilters: DeviceFilters;
  @Output() deviceSelectionChange: EventEmitter<EntityWithUuid | EntityWithUuid[]>;
  @Output() deviceFiltersChange: EventEmitter<DeviceFilters>;

  public devices: Device[];
  public totalDevices: number;
  public loadingDevices: boolean;
  public first: number;
  public rows: number;
  public categories: SelectItem<string>[];
  public organizations: Organization[];

  constructor(
    private devicesService: DevicesService,
    private tenantsService: TenantsService,
    private confirmationService: ConfirmationService
  ) {
    this.deviceSelectionChange = new EventEmitter<EntityWithUuid | EntityWithUuid[]>();
    this.deviceFiltersChange = new EventEmitter<DeviceFilters>();
    this.first = 0;
    this.rows = 10;
    this.devices = new Array(this.rows);
    this.loadingDevices = false;
  }

  ngOnInit(): void {
    this.loadDevices(0, this.rows);
    this.loadDeviceCategories();
    this.loadOrganizations();
  }

  private loadDevices(offset: number, limit: number): void {
    this.loadingDevices = true;
    this.deviceFilters.tenantUuids = [this.tenant.uuid];
    this.deviceFilters.organizationUuids = this.organization ? [this.organization.uuid] : this.deviceFilters.organizationUuids;
    this.devicesService.getDevices(offset, limit, this.deviceFilters).subscribe(response => {
      this.devices = response.devices;
      this.totalDevices = response.totalElements;
      this.loadingDevices = false;
    });
  }

  private loadDeviceCategories(): void {
    this.tenantsService.getTenantDeviceCategories(this.tenant.uuid).subscribe(response => {
      this.categories = response.deviceCategories
        .map((category: DeviceCategory) => category.name)
        .sort()
        .map((value: string) => ({value}));
    });
  }

  private loadOrganizations(): void {
    this.tenantsService.getTenantOrganizationSummaries(this.tenant.uuid).subscribe(value => {
      this.organizations = value.organizations;
    });
  }

  public applyFilters(): void {
    this.first = 0;
    this.loadDevices(this.first / this.rows, this.rows);
  }

  public onDevicePageChange(event: any): void {
    this.deviceFilters.sortField = event.sortField;
    this.deviceFilters.sortOrder = event.sortOrder <= 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadDevices(event.first / event.rows, event.rows);
  }

  public displaySelectAllDialog(): void {
    this.confirmationService.confirm({
      message: translate('info.confirmSelectAll', { totalElements: this.totalDevices }),
      header: translate('common.selectAll'),
      acceptLabel: translate('common.accept'),
      rejectLabel: translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.selectAll()
    });
  }

  public selectAll(): void {
    this.deviceFilters.tenantUuids = [this.tenant.uuid];
    this.deviceFilters.organizationUuids = this.organization ? [this.organization.uuid] : this.deviceFilters.organizationUuids;
    this.devicesService.getDevices(null, null, this.deviceFilters).subscribe(response => {
      this.deviceSelection = [...this.deviceSelection as EntityWithUuid[]];
      this.deviceSelection.push(...response.devices);
      this.deviceSelectionChange.emit(this.deviceSelection);
    });
  }

}
